import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import "firebase/compat/firestore";

import { getRecoil } from "recoil-nexus";
import { nanoid } from "nanoid";
import _ from "underscore";
import { sendMessageApi } from "../services/messagingApi";

import organizationIdState from "../atoms/organizationIdSelector";
import userDataState from "../atoms/userDataAtom";
import { UserType } from "./OrganizationData";
import { consola } from "consola";
import { determineAudioExtension } from "../utils/audio";
import {
  getVideoPolicy,
  bytesToMegabytes,
} from "../../../../shared/config/recordingPolicy";

export const AppDestination = {
  messaging: "messaging",
  dashboard: "dashboard",
};

export const GroupType = {
  alert: "alert",
  group: "group",
  questions: "questions",
  single: "single",
};

export type GetMessageSubscriptionProps = {
  groupId: string;
  limit: number;
  filter: string | null;
  onMessages: (snapshot: firebase.firestore.DocumentData[]) => void;
};

export type GetMessageHistorySubscriptionProps = {
  uid: string;
  limit: number;
  onMessages: (snapshot: firebase.firestore.DocumentData[]) => void;
};

export type MessageHistorySentProps = {
  messageId: string;
  uid: string;
};

export type GetMessageSubscriptionForUserProps = {
  toUID: string;
  fromUID: string;
  onMessage: (snapshot: firebase.firestore.DocumentData[]) => void;
};

export async function getMessageGroupWithUID(
  users: string[],
  type = GroupType.single,
) {
  const organizationId = getRecoil(organizationIdState);

  const groupRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("messageGroups")
    .where("users", "array-contains-any", users)
    .where("type", "==", type);
  consola.debug("++++++ READ -> MessageData: getMEssageGroupWithUID");
  const groups = await groupRef.get();
  return groups.docs.map((d) => {
    return d.data();
  });
}

/**
 * Send a text message to a group.
 *
 * Delegates to the /api/v1/message/send endpoint which atomically writes
 * the message document and updates the group's lastMessage metadata in a
 * single Firestore batch, eliminating the group-read + two-write pattern
 * that left the conversation list showing stale data on a failed second write.
 */
export async function sendMessageToGroup(
  groupId: string,
  message: string,
  appDestination = AppDestination.messaging,
) {
  if (!groupId || !message || message.length === 0) {
    throw new Error("Missing required data in sendMessageToGroup");
  }

  await sendMessageApi({ groupId, message, appDestination });
  consola.debug("MessageData: sendMessageToGroup via API", { groupId });
}

export async function sendMessageToUsersGroup({
  uid,
  message,
  mustBeRegistered = true,
}: {
  uid: string;
  message: string;
  mustBeRegistered?: boolean;
}) {
  const userRef = firebase.firestore().collection("users").doc(uid);

  consola.debug("++++++ READ -> MessageData: sendMessageToUsersGroup");
  const user = await userRef.get();
  if (user.exists) {
    const userData = user.data();
    if (userData) {
      // get client record
      const clientRef = firebase
        .firestore()
        .collection("organizations")
        .doc(userData.organizationId)
        .collection("clients")
        .doc(uid);
      const client = await clientRef.get();
      if (client.exists) {
        const clientData = client.data();
        if (clientData) {
          const override = import.meta.env.VITE_OVERRIDE_SEND_MESSAGE || false;
          if (mustBeRegistered && !clientData.authId && !override) {
            consola.log("User is not registered, not sending message");
            return;
          }

          const messageGroups = await getMessageGroupWithUID([uid]);
          if (messageGroups.length > 0) {
            const messageGroup = messageGroups[0];

            sendMessageToGroup(messageGroup.id, message);
          }
        }
      }
    }
  }
}

export async function updateMessageViewedBy({
  groupId,
  messageId,
  userId,
}: {
  groupId: string;
  messageId: string;
  userId: string;
}) {
  const organizationId = getRecoil(organizationIdState);

  const messageRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("messageGroups")
    .doc(groupId)
    .collection("messages")
    .doc(messageId);
  consola.debug("++++++ READ -> MessageData: upateMessageViewedBy");
  const message = await messageRef.get();
  if (message && message.exists) {
    const messageValue = message.data();
    if (messageValue) {
      const viewedBy = messageValue.viewedBy;
      let viewedByNew = [];
      if (viewedBy) {
        viewedByNew = viewedBy;
      }
      const index = _.findIndex(viewedByNew, { uid: userId });
      if (index === -1) {
        viewedByNew.push({
          uid: userId,
          viewedOn: new Date(),
        });
        consola.info("+++++ WRITE => MessageData: updateMessageViewedBy");
        await messageRef.set(
          {
            viewedBy: viewedByNew,
          },
          { merge: true },
        );
      }
    }
  }
}

export async function updateMessageGroupUserActivity(
  uid: string,
  messageGroupId: string,
  source?: string | null,
) {
  if (!messageGroupId) {
    consola.error(new Error("Missing parameter messageGroupId"));
  }

  const organizationId = getRecoil(organizationIdState);

  // userDataValues.uid
  const activityRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("messageGroupActivity")
    .doc(`${messageGroupId}_${uid}`);
  consola.info("+++++ WRITE => MessageData: updateMessageGroupUserActivity");
  await activityRef.set(
    {
      lastSeenOn: new Date(),
      uid: uid,
      messageGroupId: messageGroupId,
      source: source ? source : "unknown",
    },
    { merge: true },
  );
}

const MessageData = {
  getMessagesSubscription: async ({
    groupId,
    limit = 30,
    filter = null,
    onMessages,
  }: GetMessageSubscriptionProps) => {
    if (!groupId) {
      return null;
    }
    const organizationId = getRecoil(organizationIdState);

    let messagesRef;

    if (!filter) {
      messagesRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .orderBy("createdOn", "desc")
        .limit(limit);
    } else {
      // get array of strings from filter
      const filterArray = filter.split(" ");

      messagesRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .where("keywords", "array-contains-any", filterArray)
        .orderBy("createdOn", "desc")
        .limit(limit);
    }

    return await messagesRef.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> messageData: getMessagesSubscription");
      if (snapshot && snapshot.docs && snapshot.docs.length > 0) {
        let docs = snapshot.docs;
        // filter docs that do not have the exact filter string
        if (filter && filter.length > 0) {
          const filtered = snapshot.docs.filter((doc) => {
            const docData = doc.data();
            if (docData) {
              const message = docData.message;
              const messageType = docData.messageType;
              // message contains the filter string
              if (messageType === "text") {
                if (
                  message &&
                  message.toLowerCase().includes(filter.toLowerCase())
                ) {
                  return true;
                }
              } else {
                return true;
              }
            }
            return false;
          });
          docs = filtered;
        }

        onMessages(docs);
      } else {
        onMessages([]);
      }
    });
  },
  getMessagesHistorySubscription: ({
    uid,
    limit = 30,
    onMessages,
  }: GetMessageHistorySubscriptionProps) => {
    const organizationId = getRecoil(organizationIdState);

    const messagesHistoryRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("messageHistory")
      .where("users", "array-contains", uid)
      .where("message.fromUID", "!=", uid)
      .orderBy("message.fromUID", "asc")
      .orderBy("createdOn", "desc")
      .limit(limit);

    return messagesHistoryRef.onSnapshot((snapshot) => {
      consola.debug(
        "++++++ SNAPSHOT -> messageData: getMessagesHistorySubscription",
      );
      if (snapshot && snapshot.docs) {
        // filter out docs that have the current user in the notificationUsers array
        const filtered = snapshot.docs.filter((doc) => {
          const docData = doc.data();
          if (docData) {
            const notificationUsers = docData.notificationUsers;
            if (notificationUsers) {
              const index = notificationUsers.indexOf(uid);
              if (index === -1) {
                return true;
              }
            }
          }
          return false;
        });

        onMessages(filtered);
      }
    });
  },
  markMessageHistorySent: async ({
    messageId,
    uid,
  }: MessageHistorySentProps) => {
    const organizationId = getRecoil(organizationIdState);

    const messagesHistoryDocRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("messageHistory")
      .doc(messageId);
    consola.debug("++++++ READ -> MessageData: markMessageHistorySent");
    const messagesHistoryDoc = await messagesHistoryDocRef.get();
    if (messagesHistoryDoc.exists) {
      const messagesHistoryDocData = messagesHistoryDoc.data();
      if (messagesHistoryDocData) {
        // set user array with current user
        consola.info("+++++ WRITE => MessageData: markMessageHistorySent");
        await messagesHistoryDocRef.set(
          {
            notificationUsers: [
              uid,
              ...messagesHistoryDocData.notificationUsers,
            ],
          },
          { merge: true },
        );
      }
    }
  },
  getMessageSubscriptionForUser: async (
    toUID: string,
    fromUID: string,
    onMessage: (snapshot: firebase.firestore.DocumentData[]) => void,
  ) => {
    const organizationId = getRecoil(organizationIdState);

    let messageRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("messages")
      .where("toUID", "==", toUID);

    if (fromUID) {
      messageRef = messageRef.where("fromUID", "==", fromUID);
    }

    return messageRef.onSnapshot(async (snapshot) => {
      consola.info(
        "++++++ SNAPSHOT -> messageData: getMessageSubscriptionForUser",
      );
      if (onMessage) {
        onMessage(snapshot.docs);
      }
    });
  },
  editMessageGroup: async (
    groupId: string,
    input: Partial<firebase.firestore.DocumentData>,
  ) => {
    const organizationId = getRecoil(organizationIdState);
    const groupRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("messageGroups")
      .doc(groupId);
    consola.debug("++++++ READ -> MessageData: editMessageGroup");
    const group = await groupRef.get();
    if (group.exists) {
      consola.info("+++++ WRITE => MessageData: editMessageGroup");
      await groupRef.set(input, { merge: true });
    }
  },
  addMessageGroup: async (input: Partial<firebase.firestore.DocumentData>) => {
    const groupId = nanoid();

    const organizationId = getRecoil(organizationIdState);

    const groupRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("messageGroups")
      .doc(groupId);

    const data = {
      id: groupId,
      ...input,
    };
    consola.info("+++++ WRITE => MessageData: addMessageGroup");
    await groupRef.set(data, { merge: true });
    return data;
  },
  getMessageGroup: async (
    userId: string,
    type = GroupType.single,
    order = false,
  ) => {
    const organizationId = getRecoil(organizationIdState);

    let groupRef: any = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("messageGroups");

    if (type === "type") {
      groupRef = groupRef.where("type", "==", type);
      groupRef = groupRef.orderBy("type");
    }
    if (type === GroupType.single) {
      groupRef = groupRef.where("userId", "==", userId);
      if (order) {
        groupRef = groupRef.orderBy("lastDate");
      }
    }
    if (type === UserType.coach) {
      groupRef = groupRef.orderBy("lastDate", "desc");
    }
    if (type === "all") {
      groupRef = groupRef.orderBy("type");
    }
    consola.debug("++++++ READ -> MessageData: getMessageGroup");
    const groups = await groupRef.get();

    let groupDataArray = groups.docs.map((d: any) => {
      return {
        ...d.data(),
        ref: d.ref,
      };
    });

    // Filter based on type and user.
    if (type !== GroupType.single && type !== "type" && type !== "all") {
      groupDataArray = _.filter(groupDataArray, (d) => {
        if (d.users) {
          return d.users.includes(userId);
        }
        return true;
      });
    }

    return groupDataArray;
  },
  sendMessageToGroup: async (
    groupId: string,
    message: string,
    appDestination = AppDestination.messaging,
  ) => {
    if (!groupId || !message || message.length === 0) {
      return;
    }

    const batch = firebase.firestore().batch();

    const organizationId = getRecoil(organizationIdState);
    const userState = getRecoil<any>(userDataState);

    const messageId = nanoid();

    const groupRef = firebase
      .firestore()
      .doc(`organizations/${organizationId}/messageGroups/${groupId}`);

    consola.info(
      "++++++ READ -> MessageData: sendMessageToGroup",
      groupId,
      userState.uid,
    );

    const group = await groupRef.get();
    if (group.exists) {
      const messageRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .doc(messageId);
      consola.info("+++++ WRITE => MessageData: sendMessageToGroup");
      batch.set(
        messageRef,
        {
          id: messageId,
          createdOn: new Date(),
          fromUID: userState.uid,
          groupId: groupId,
          fromUser: userState,
          messageType: "text",
          processed: false,
          processedOn: null,
          viewedBy: [],
          appDestination,
          message: message,
        },
        { merge: true },
      );

      batch.update(groupRef, {
        lastMessage: message,
        lastUser: userState,
        lastDate: new Date(),
      });
      consola.info("+++++ WRITE => MessageData: sendMessageToGroup");
      await batch.commit();
    }
  },
  sendAudioMessageToGroup: async (
    groupId: string,
    blob: Blob,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _buffer?: ArrayBuffer,
    duration?: string,
  ) => {
    if (!groupId || !blob) {
      return;
    }

    try {
      const organizationId = getRecoil(organizationIdState);
      const userState = getRecoil<any>(userDataState);

      const messageId = nanoid();

      const groupRef = firebase
        .firestore()
        .doc(`organizations/${organizationId}/messageGroups/${groupId}`);

      consola.debug("++++++ READ -> MessageData: sendAudioMessageToGroup");
      const group = await groupRef.get();
      if (!group.exists) {
        throw new Error('Message group does not exist');
      }

      const messageRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .doc(messageId);

      const extension = determineAudioExtension(blob.type);
      const path = `/${organizationId}/messageGroups/${groupId}/${messageId}.${extension}`;

      const metadata = {
        cacheControl: 'private, max-age=604800',
        contentType: blob.type || 'audio/mp4', // Default to AAC if blob type missing
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET',
        }
      };

      consola.info("Uploading audio blob to Firebase Storage", { 
        path, 
        blobType: blob.type, 
        blobSize: blob.size,
        contentType: metadata.contentType
      });

      // Upload the blob directly to Firebase Storage
      const uploadTask = firebase
        .storage()
        .ref(path)
        .put(blob, metadata);

      await uploadTask;

      // Get the download URL
      const url = await firebase
        .storage()
        .ref(path)
        .getDownloadURL();

      consola.info("Audio uploaded successfully", { url });

      // Create the message document
      const batch = firebase.firestore().batch();

      const data: Record<string, unknown> = {
        id: messageId,
        createdOn: new Date(),
        fromUID: userState.uid,
        groupId: groupId,
        fromUser: userState,
        messageType: "audio",
        processed: false,
        processedOn: null,
        viewedBy: [],
        appDestination: AppDestination.messaging,
        audioUrl: url,
        message: "",
      };

      if (duration) {
        data.duration = duration;
      }

      batch.set(messageRef, data, { merge: true });

      batch.update(groupRef, {
        lastMessage: "Audio message.",
        lastUser: userState,
        lastDate: new Date(),
      });

      consola.info("+++++ WRITE => MessageData: sendAudioMessageToGroup");
      await batch.commit();

      consola.info("Audio message saved successfully");

    } catch (error) {
      consola.error("Failed to send audio message:", error);
      throw error;
    }
  },

  sendImageMessageToGroup: async (
    groupId: string,
    file: File
  ) => {
    const organizationId = getRecoil(organizationIdState);
    const userState = getRecoil<any>(userDataState);
    
    if (!organizationId || !userState) {
      throw new Error("Organization ID or user state not available");
    }

    const messageId = nanoid();
    const extension = file.name.split('.').pop() || 'jpg';
    const path = `/organizations/${organizationId}/messageGroups/${groupId}/images/${messageId}.${extension}`;

    try {
      // Upload image to Firebase Storage
      const storageRef = firebase.storage().ref(path);
      const metadata = {
        cacheControl: "private, max-age=645000",
        contentType: file.type,
      };
      
      const uploadTask = storageRef.put(file, metadata);
      await uploadTask;
      
      const url = await storageRef.getDownloadURL();

      // Create message document
      const batch = firebase.firestore().batch();
      const messageRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .doc(messageId);

      const groupRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId);

      const data = {
        id: messageId,
        createdOn: new Date(),
        fromUID: userState.uid,
        groupId: groupId,
        fromUser: userState,
        messageType: "image",
        processed: false,
        processedOn: null,
        viewedBy: [],
        appDestination: AppDestination.messaging,
        imageUrl: url,
        message: "",
      };

      batch.set(messageRef, data, { merge: true });
      batch.update(groupRef, {
        lastMessage: "Image shared",
        lastUser: userState,
        lastDate: new Date(),
      });
      
      await batch.commit();
      consola.info("+++++ WRITE => MessageData: sendImageMessageToGroup");
    } catch (error) {
      consola.error("Failed to send image message:", error);
      throw error;
    }
  },

  sendVideoMessageToGroup: async (
    groupId: string,
    file: File
  ) => {
    const organizationId = getRecoil(organizationIdState);
    const userState = getRecoil<any>(userDataState);
    
    if (!organizationId || !userState) {
      throw new Error("Organization ID or user state not available");
    }

    const messageId = nanoid();
    const extension = file.name.split('.').pop() || 'mp4';
    const path = `/organizations/${organizationId}/messageGroups/${groupId}/videos/${messageId}.${extension}`;
    const videoPolicy = getVideoPolicy();
    const maxVideoSizeBytes = videoPolicy.maxFileSizeBytes;

    try {
      if (maxVideoSizeBytes > 0 && file.size > maxVideoSizeBytes) {
        const sizeMbLabel = bytesToMegabytes(file.size).toFixed(1);
        const limitMbLabel = bytesToMegabytes(maxVideoSizeBytes).toFixed(1);
        throw new Error(
          `Video file is ${sizeMbLabel}MB but must be under ${limitMbLabel}MB. Please trim or compress the clip before uploading.`,
        );
      }

      // Upload video to Firebase Storage
      const storageRef = firebase.storage().ref(path);
      const metadata = {
        cacheControl: "private, max-age=645000",
        contentType: file.type,
      };
      
      const uploadTask = storageRef.put(file, metadata);
      await uploadTask;
      
      const url = await storageRef.getDownloadURL();

      // Create message document
      const batch = firebase.firestore().batch();
      const messageRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .doc(messageId);

      const groupRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId);

      const data = {
        id: messageId,
        createdOn: new Date(),
        fromUID: userState.uid,
        groupId: groupId,
        fromUser: userState,
        messageType: "video",
        processed: false,
        processedOn: null,
        viewedBy: [],
        appDestination: AppDestination.messaging,
        videoUrl: url,
        videoType: "shared",
        message: "",
      };

      batch.set(messageRef, data, { merge: true });
      batch.update(groupRef, {
        lastMessage: "Video shared",
        lastUser: userState,
        lastDate: new Date(),
      });
      
      await batch.commit();
      consola.info("+++++ WRITE => MessageData: sendVideoMessageToGroup");
    } catch (error) {
      consola.error("Failed to send video message:", error);
      throw error;
    }
  },

  sendDocumentMessageToGroup: async (
    groupId: string,
    file: File
  ) => {
    const organizationId = getRecoil(organizationIdState);
    const userState = getRecoil<any>(userDataState);
    
    if (!organizationId || !userState) {
      throw new Error("Organization ID or user state not available");
    }

    const messageId = nanoid();
    const sharedFileId = nanoid();
    
    try {
      // First, upload file as a shared file (for PDFs and documents)
      const extension = file.name.split('.').pop() || 'pdf';
      const path = `/organizations/${organizationId}/shared_files/${sharedFileId}.${extension}`;

      const storageRef = firebase.storage().ref(path);
      const metadata = {
        cacheControl: "private, max-age=645000",
        contentType: file.type,
      };
      
      const uploadTask = storageRef.put(file, metadata);
      await uploadTask;
      
      const url = await storageRef.getDownloadURL();

      // Create batch for both shared file and message
      const batch = firebase.firestore().batch();

      // Create shared file document
      const sharedFileRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("sharedFiles")
        .doc(sharedFileId);

      const sharedFileData = {
        id: sharedFileId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        extension: extension,
        url: url,
        uploadedBy: userState.uid,
        uploadedByUser: userState,
        uploadedOn: new Date(),
        isCoach: userState.userType === UserType.coach,
        metadata: {
          originalName: file.name,
          contentType: file.type,
          size: file.size,
        },
      };

      batch.set(sharedFileRef, sharedFileData);

      // Create message document
      const messageRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .doc(messageId);

      const groupRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId);

      const messageData = {
        id: messageId,
        createdOn: new Date(),
        fromUID: userState.uid,
        groupId: groupId,
        fromUser: userState,
        messageType: file.type.includes('pdf') ? "pdf" : "file",
        processed: false,
        processedOn: null,
        viewedBy: [],
        appDestination: AppDestination.messaging,
        pdfUrl: file.type.includes('pdf') ? url : undefined,
        fileUrl: !file.type.includes('pdf') ? url : undefined,
        fileName: file.name,
        fileSize: file.size,
        sharedFileId: sharedFileId,
        message: "",
      };

      batch.set(messageRef, messageData, { merge: true });
      batch.update(groupRef, {
        lastMessage: `${file.type.includes('pdf') ? 'PDF' : 'File'} shared: ${file.name}`,
        lastUser: userState,
        lastDate: new Date(),
      });
      
      await batch.commit();
      consola.info("+++++ WRITE => MessageData: sendDocumentMessageToGroup");
    } catch (error) {
      consola.error("Failed to send document message:", error);
      throw error;
    }
  },
  getMessageGroupById: async (groupId: string) => {
    const organizationId = getRecoil(organizationIdState);
    
    const groupRef = firebase
      .firestore()
      .doc(`organizations/${organizationId}/messageGroups/${groupId}`);
    
    consola.debug("++++++ READ -> MessageData: getMessageGroupById");
    const group = await groupRef.get();
    
    if (group.exists) {
      return group.data();
    }
    
    return null;
  },
  deleteMessage: async (groupId: string, messageId: string) => {
    const organizationId = getRecoil(organizationIdState);
    const userState = getRecoil<any>(userDataState);
    
    consola.info("+++++ DELETE => MessageData: deleteMessage - Debug info", {
      organizationId,
      userState: userState ? {
        uid: userState.uid,
        type: userState.type,
        userType: userState.userType,
        role: userState.role
      } : null,
      groupId,
      messageId
    });
    
    if (!organizationId || !userState) {
      throw new Error("Organization ID or user state not available");
    }
    
    // Check if user is a coach (has permission to delete messages)
    // Check both possible property names for user type
    const isCoach = userState.type === UserType.coach || userState.userType === UserType.coach;
    const isAdmin = userState.role === "admin";
    
    if (!isCoach && !isAdmin) {
      throw new Error(`Only coaches can delete messages. User type: ${userState.type || userState.userType}, Role: ${userState.role}`);
    }
    
    try {
      const messageRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("messageGroups")
        .doc(groupId)
        .collection("messages")
        .doc(messageId);
      
      consola.info("+++++ DELETE => MessageData: deleteMessage", {
        groupId,
        messageId,
        userId: userState.uid
      });
      
      // Delete the message document (this will trigger the Firebase function)
      await messageRef.delete();
      
      consola.info("Message deleted successfully");
    } catch (error) {
      consola.error("Failed to delete message:", error);
      throw error;
    }
  },
};

export default MessageData;

// Export standalone function for hooks
export const getMessageGroupById = MessageData.getMessageGroupById;
export const deleteMessage = MessageData.deleteMessage;
